import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useConfig } from '../context/ConfigContext';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../api/axios';

const EventAttendance = () => {
    const { config } = useConfig();
    const { user, updateUser } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const [statusMessage, setStatusMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [confirmedState, setConfirmedState] = useState(false);
    const [selectedCity, setSelectedCity] = useState('');

    useEffect(() => {
        const city = searchParams.get('city') || searchParams.get('ciudad') || sessionStorage.getItem('pending_attendance_city') || user?.city || '';
        if (city) {
            setSelectedCity(city);
        }
        if (user?.asistire || user?.asistencia_evento) {
            setConfirmedState(true);
            setStatusMessage('¡Tu asistencia ya ha sido registrada previamente!');
        }
    }, [searchParams, user]);

    const pageDesign = config?.pageDesigns?.attendance || {};
    const secondaryColor = config?.secondaryColor || '#ff5874';
    const tertiaryColor = config?.tertiaryColor || '#008b8b';
    const backgroundColor = config?.backgroundColor || '#00484c';

    const attendanceTitle = pageDesign.title || "Confirmación de Ingreso al Evento";
    const titleColor = pageDesign.titleColor || secondaryColor;
    
    // Dynamic event details
    const eventTitle = pageDesign.event_name || config?.eventName || "Evento Pfizer VRS";
    const eventText = pageDesign.event_text || "Confirmación de Asistencia Presencial en el Evento";
    const locationText = selectedCity 
        ? `Sede Registrada: ${selectedCity}` 
        : (pageDesign.location_text || "Sede del Evento Presencial");

    const confirmBtnText = pageDesign.confirm_btn_text || "Confirmar Ingreso al Evento";
    const denyBtnText = pageDesign.deny_btn_text || "Cancelar";

    const handleConfirm = async (confirmed) => {
        if (confirmed && !selectedCity) {
            setStatusMessage('Por favor seleccione una ciudad de la lista desplegable.');
            return;
        }

        setIsSubmitting(true);
        setStatusMessage('');
        try {
            if (confirmed) {
                const res = await api.post('/attendance/self-confirm', {
                    city: selectedCity || null,
                    method: 'url_link',
                    record_type: 'asistencia_en_el_evento',
                    userId: user?.id || null
                });

                const updatedUserData = {
                    ...(user || {}),
                    ...(res.data?.user || {}),
                    asistencia_evento: true,
                    asistire: true
                };
                updateUser(updatedUserData);

                setStatusMessage('¡Asistencia en el evento registrada con éxito en tu perfil!');
                setConfirmedState(true);
                sessionStorage.removeItem('pending_email_attendance');
                sessionStorage.removeItem('pending_email_attendance_city');
                sessionStorage.setItem('event_attendance_confirmed', 'true');
            } else {
                sessionStorage.removeItem('pending_email_attendance');
                sessionStorage.removeItem('pending_email_attendance_city');
                setStatusMessage('Respuesta registrada.');
                setTimeout(() => {
                    navigate(user ? '/' : '/login');
                }, 1200);
            }
        } catch (err) {
            console.error("Event attendance confirm error", err);
            if (confirmed) {
                setConfirmedState(true);
                setStatusMessage('¡Asistencia en el evento registrada exitosamente!');
            } else {
                setStatusMessage('Respuesta registrada.');
                setTimeout(() => navigate('/login'), 1200);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const bgImage = pageDesign.bgImage || config?.pageDesigns?.home?.bgImage;

    return (
        <div 
            className="min-h-screen flex flex-col justify-between text-white relative overflow-hidden bg-cover bg-center transition-all duration-700"
            style={{ 
                backgroundColor: backgroundColor,
                backgroundImage: bgImage ? `url(${bgImage})` : 'none'
            }}
        >
            <Header title={attendanceTitle} />

            <main className="flex-grow flex items-center justify-center p-4 z-10 my-6">
                <div className="w-full max-w-xl bg-white/10 backdrop-blur-xl p-8 sm:p-10 rounded-[35px] border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.3)] flex flex-col items-center text-center space-y-6">

                    {/* Header Badge */}
                    <div className="inline-block px-5 py-2 rounded-full bg-emerald-500/20 border border-emerald-400/30 backdrop-blur-md">
                        <span className="text-xs font-black uppercase tracking-widest text-emerald-300">
                            🎟️ Asistencia Presencial en el Evento
                        </span>
                    </div>

                    <h1 
                        className="text-2xl sm:text-3xl font-black capitalize tracking-wide leading-snug"
                        style={{ color: titleColor }}
                    >
                        {attendanceTitle}
                    </h1>

                    <div className="w-full space-y-4 bg-black/20 p-6 rounded-2xl border border-white/10 text-slate-100 text-sm font-medium">
                        <p className="text-base font-bold text-white leading-relaxed">
                            {eventTitle}
                        </p>
                        <p className="text-xs text-slate-300">{eventText}</p>

                        {/* City Selector Dropdown */}
                        <div className="text-left pt-2">
                            <label className="block text-[11px] font-black capitalize tracking-widest text-slate-300 mb-2 pl-1">
                                Selecciona tu Ciudad de Asistencia:
                            </label>
                            <select
                                value={selectedCity}
                                onChange={(e) => setSelectedCity(e.target.value)}
                                className="w-full bg-slate-900/80 border border-white/20 p-4 rounded-2xl text-white font-bold text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all appearance-none"
                            >
                                <option value="" disabled>-- Selecciona una ciudad --</option>
                                <option value="Arequipa">Arequipa</option>
                                <option value="Piura">Piura</option>
                                <option value="Lima">Lima</option>
                            </select>
                        </div>

                        {user && (
                            <div className="pt-2 border-t border-white/10 flex items-center justify-center gap-3">
                                <span className="text-xs font-bold text-slate-300">Asistente:</span>
                                <span className="text-xs font-black text-white">{user.name} {user.lastName} ({user.identification})</span>
                            </div>
                        )}
                    </div>

                    {statusMessage && (
                        <div className="p-4 bg-emerald-500/20 border border-emerald-400/40 rounded-2xl text-emerald-200 text-sm font-bold animate-fadeIn">
                            {statusMessage}
                        </div>
                    )}

                    {!confirmedState ? (
                        <div className="w-full space-y-4 pt-4">
                            <button
                                onClick={() => handleConfirm(true)}
                                disabled={isSubmitting || !selectedCity}
                                style={{ backgroundColor: secondaryColor }}
                                className="w-full text-white py-4 px-6 rounded-2xl font-black capitalize tracking-widest text-sm shadow-xl hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-50"
                            >
                                {isSubmitting ? 'Procesando...' : `✓ ${confirmBtnText}`}
                            </button>

                            <button
                                onClick={() => handleConfirm(false)}
                                disabled={isSubmitting}
                                className="w-full bg-slate-800/80 hover:bg-slate-800 text-slate-300 py-3.5 px-6 rounded-2xl font-bold capitalize tracking-widest text-xs transition-all border border-white/10"
                            >
                                ✕ {denyBtnText}
                            </button>
                        </div>
                    ) : (
                        <div className="w-full space-y-6 pt-4 animate-fadeIn">
                            <div className="p-6 bg-emerald-900/40 border border-emerald-500/30 rounded-3xl space-y-3">
                                <h3 className="text-lg font-black uppercase tracking-wide text-emerald-300">
                                    ¡Ingreso al Evento Confirmado!
                                </h3>
                                <p className="text-xs text-slate-200 leading-relaxed font-medium">
                                    Tu presencia en el evento ha sido guardada exitosamente y anclada a tu perfil de usuario.
                                </p>
                            </div>

                            <div className="pt-2">
                                <button
                                    onClick={() => navigate(user ? '/' : '/login')}
                                    style={{ backgroundColor: tertiaryColor }}
                                    className="px-8 py-3.5 rounded-2xl font-black uppercase tracking-widest text-xs text-white shadow-lg hover:scale-105 transition-all"
                                >
                                    {user ? 'Ir al Menú Principal' : 'Iniciar Sesión'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <Footer />
        </div>
    );
};

export default EventAttendance;
